import { APP_VERSION } from "../appVersion";
import { dispatchLocalWithdrawalUpdated, type LocalWithdrawalUpdateReason } from "./localWithdrawalEvents";
import { failRuntimeActivity, finishRuntimeActivity, startRuntimeActivity } from "./runtimeActivityService";
import { getPwaTabId, publishUpdateCoordination, subscribeUpdateCoordination, tryAcquireUpdateLease, UPDATE_COORDINATION_LEASE_MS } from "./pwaUpdateCoordination";
const LAST_ATTEMPT_KEY = "materialentnahme.sw.lastAttemptAt";
const ERROR_NAME_KEY = "materialentnahme.sw.errorName";
const ERROR_MESSAGE_KEY = "materialentnahme.sw.errorMessage";
const DISMISSED_APP_UPDATE_KEY = "materialentnahme.appUpdate.dismissedVersion";
const INSTALLED_APP_VERSION_KEY = "materialentnahme.appUpdate.installedVersion";
const APP_UPDATE_SUCCESS_ISLAND_SHOWN_KEY = "materialentnahme.appUpdate.successIslandShownVersion";
const PENDING_APP_UPDATE_TARGET_KEY = "materialentnahme.appUpdate.pendingTargetVersion";
const PENDING_APP_UPDATE_RECOVERY_KEY = "materialentnahme.appUpdate.recoveryAttempt";
const PENDING_APP_UPDATE_STARTED_AT_KEY = "materialentnahme.appUpdate.startedAt";
const PENDING_APP_UPDATE_RECOVERY_TTL_MS = 120_000;
const PENDING_APP_UPDATE_OWNER_KEY = "materialentnahme.appUpdate.pendingOwnerTabId";
const RELOAD_GUARD_KEY = "materialentnahme.appUpdate.reloadGuard";
const UPDATE_EVENT_LOG_KEY = "materialentnahme.appUpdate.eventLog";
const UPDATE_EVENT_LOG_LIMIT = 24;
const RELOAD_GUARD_TTL_MS = 120_000;

const UPDATE_META_CACHE = "materialentnahme-update-meta";
const UPDATE_META_URL = "/__materialentnahme_update_meta__";

export interface CompletedAppUpdate {
  fromVersion: string;
  toVersion: string;
  completedAt: string;
}

export interface ServiceWorkerUpdateEvent {
  at: string;
  type: string;
  detail: string | null;
  targetVersion: string | null;
}

export interface ServiceWorkerDiagnostics {
  supported: boolean;
  secureContext: boolean;
  origin: string;
  registrationState: "aktiv" | "wartend" | "installiert" | "nicht registriert" | "nicht unterstützt" | "fehler";
  scope: string | null;
  controllerActive: boolean;
  scriptReachable: boolean | null;
  lastAttemptAt: string | null;
  errorName: string | null;
  errorMessage: string | null;
  bundleVersion: string;
  controllerVersion: string | null;
  publishedVersion: string | null;
  versionsMatch: boolean | null;
  installingVersion: string | null;
  waitingVersion: string | null;
  pendingTargetVersion: string | null;
  pendingOwnedByThisTab: boolean;
  restartRequired: boolean;
  updateCheckFailures: number;
  lastUpdateCheckAt: string | null;
  recentUpdateEvents: ServiceWorkerUpdateEvent[];
  appShellCacheVersions: string[];
  runtimeAssetCachePresent: boolean;
}

export interface AppUpdateState {
  available: boolean;
  applying: boolean;
  phase: "idle" | "preparing" | "activating" | "reloading";
  error: string | null;
  targetVersion: string | null;
  noticeDismissed: boolean;
  restartRequired: boolean;
  coordinatedByOtherTab: boolean;
}

const updateListeners = new Set<() => void>();
let updateState: AppUpdateState = { available: false, applying: false, phase: "idle", error: null, targetVersion: null, noticeDismissed: false, restartRequired: false, coordinatedByOtherTab: false };
let observedRegistration: ServiceWorkerRegistration | null = null;
let lifecycleBound = false;
let serverUpdateAvailable = false;
let serverTargetVersion: string | null = null;

const UPDATE_CHECK_BASE_INTERVAL_MS = 5 * 60_000;
const UPDATE_CHECK_JITTER_MAX_MS = 45_000;
const UPDATE_CHECK_MIN_GAP_MS = 12_000;
const UPDATE_CHECK_MAX_BACKOFF_MS = 30 * 60_000;
let updateCheckPromise: Promise<string | null> | null = null;
let updateCheckTimer: number | null = null;
let lastUpdateCheckAt = 0;
let updateCheckFailures = 0;

function emitUpdateState(next: Partial<AppUpdateState>): void {
  updateState = { ...updateState, ...next };
  for (const listener of updateListeners) listener();
}

export function getAppUpdateState(): AppUpdateState {
  return updateState;
}

export function subscribeAppUpdate(listener: () => void): () => void {
  updateListeners.add(listener);
  return () => updateListeners.delete(listener);
}

export async function getCompletedMaterialentnahmeAppUpdate(): Promise<CompletedAppUpdate | null> {
  if (!("caches" in window)) return null;
  try {
    const cache = await caches.open(UPDATE_META_CACHE);
    const response = await cache.match(UPDATE_META_URL);
    if (!response) return null;
    const payload = await response.json() as Partial<CompletedAppUpdate>;
    if (!payload.fromVersion || !payload.toVersion || !payload.completedAt) return null;
    if (payload.toVersion !== APP_VERSION || payload.fromVersion === payload.toVersion) return null;
    return { fromVersion: payload.fromVersion, toVersion: payload.toVersion, completedAt: payload.completedAt };
  } catch {
    return null;
  }
}

export async function clearCompletedMaterialentnahmeAppUpdate(): Promise<void> {
  if (!("caches" in window)) return;
  try {
    const cache = await caches.open(UPDATE_META_CACHE);
    await cache.delete(UPDATE_META_URL);
  } catch {
    // Die Meldung darf auch dann quittiert werden, wenn der Metacache nicht verfügbar ist.
  }
}

function cleanAppUpdateCacheBuster(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("__app_update")) return;
    url.searchParams.delete("__app_update");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // Rein kosmetisch; ein verbliebener Cache-Buster ist funktional harmlos.
  }
}

function safeStorageGet(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function safeStorageSet(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* Diagnose darf Registrierung nie blockieren. */ }
}

function safeStorageRemove(key: string): void {
  try { window.localStorage.removeItem(key); } catch { /* ignore */ }
}

function readUpdateEventLog(): ServiceWorkerUpdateEvent[] {
  const raw = safeStorageGet(UPDATE_EVENT_LOG_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is ServiceWorkerUpdateEvent => Boolean(
      entry && typeof entry === "object"
      && typeof (entry as Partial<ServiceWorkerUpdateEvent>).at === "string"
      && typeof (entry as Partial<ServiceWorkerUpdateEvent>).type === "string",
    )).slice(-UPDATE_EVENT_LOG_LIMIT);
  } catch { return []; }
}

function recordUpdateEvent(type: string, detail: string | null = null, targetVersion: string | null = serverTargetVersion): void {
  const next = [...readUpdateEventLog(), { at: new Date().toISOString(), type, detail, targetVersion }].slice(-UPDATE_EVENT_LOG_LIMIT);
  safeStorageSet(UPDATE_EVENT_LOG_KEY, JSON.stringify(next));
}

function pendingUpdateOwner(): string | null {
  return safeStorageGet(PENDING_APP_UPDATE_OWNER_KEY);
}

function pendingUpdateOwnedByThisTab(): boolean {
  const owner = pendingUpdateOwner();
  return !owner || owner === getPwaTabId();
}

interface ReloadGuard { targetVersion: string; count: number; lastAt: number; }

function readReloadGuard(): ReloadGuard | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ReloadGuard>;
    if (!parsed.targetVersion || !Number.isFinite(parsed.count) || !Number.isFinite(parsed.lastAt)) return null;
    return parsed as ReloadGuard;
  } catch { return null; }
}

function clearReloadGuardIfTargetReached(): void {
  const guard = readReloadGuard();
  if (!guard || guard.targetVersion !== APP_VERSION) return;
  try { window.sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* best effort */ }
  recordUpdateEvent("reload_target_reached", `Bundle v${APP_VERSION} geladen`, APP_VERSION);
}

function reloadPublishedApp(targetVersion: string | null, source: "manual" | "recovery" | "controller"): boolean {
  const target = targetVersion ?? serverTargetVersion ?? APP_VERSION;
  const now = Date.now();
  const previous = readReloadGuard();
  if (previous?.targetVersion === target && now - previous.lastAt <= RELOAD_GUARD_TTL_MS && previous.count >= 1) {
    recordUpdateEvent("reload_loop_guard", `Automatischer Reload für v${target} blockiert · ${source}`, target);
    emitUpdateState({ applying: false, phase: "idle", available: true, restartRequired: true, targetVersion: target, error: "Reload-Schutz aktiv: Die App wurde für diese Version bereits neu geladen. Bitte die App einmal manuell schließen und erneut öffnen." });
    return false;
  }
  try { window.sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({ targetVersion: target, count: (previous?.targetVersion === target ? previous.count : 0) + 1, lastAt: now } satisfies ReloadGuard)); } catch { /* reload remains possible */ }
  recordUpdateEvent("reload_requested", `Quelle: ${source}`, target);
  const url = new URL(window.location.href);
  url.searchParams.set("__app_update", String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

function recordAttempt(): string {
  const value = new Date().toISOString();
  safeStorageSet(LAST_ATTEMPT_KEY, value);
  return value;
}

function recordError(error: unknown): void {
  const name = error instanceof DOMException || error instanceof Error ? error.name : "Unbekannter Fehler";
  const message = error instanceof Error ? error.message : String(error);
  safeStorageSet(ERROR_NAME_KEY, name);
  safeStorageSet(ERROR_MESSAGE_KEY, message);
}

function clearRecordedError(): void {
  safeStorageRemove(ERROR_NAME_KEY);
  safeStorageRemove(ERROR_MESSAGE_KEY);
}


function isNewerVersion(remote: string, local: string): boolean {
  const parse = (value: string) => value.split(".").map((part) => Number(part));
  const remoteParts = parse(remote);
  const localParts = parse(local);
  if (remoteParts.length !== 3 || localParts.length !== 3 || [...remoteParts, ...localParts].some((part) => !Number.isInteger(part))) return false;
  for (let index = 0; index < 3; index += 1) {
    if (remoteParts[index] > localParts[index]) return true;
    if (remoteParts[index] < localParts[index]) return false;
  }
  return false;
}

function markPendingUpdate(targetVersion: string | null, userInitiated = false): void {
  if (targetVersion) {
    safeStorageSet(PENDING_APP_UPDATE_TARGET_KEY, targetVersion);
    if (userInitiated) {
      safeStorageSet(PENDING_APP_UPDATE_STARTED_AT_KEY, String(Date.now()));
      safeStorageSet(PENDING_APP_UPDATE_RECOVERY_KEY, "0");
      safeStorageSet(PENDING_APP_UPDATE_OWNER_KEY, getPwaTabId());
      recordUpdateEvent("pending_update_started", `Benutzerinitiiert in diesem Tab`, targetVersion);
    }
  } else {
    safeStorageRemove(PENDING_APP_UPDATE_TARGET_KEY);
    safeStorageRemove(PENDING_APP_UPDATE_RECOVERY_KEY);
    safeStorageRemove(PENDING_APP_UPDATE_STARTED_AT_KEY);
    safeStorageRemove(PENDING_APP_UPDATE_OWNER_KEY);
  }
}

function pendingUpdateTarget(): string | null {
  const target = safeStorageGet(PENDING_APP_UPDATE_TARGET_KEY);
  if (!target || !/^\d+\.\d+\.\d+$/.test(target)) return null;
  return target;
}

function pendingUpdateIsRecent(): boolean {
  const startedAt = Number(safeStorageGet(PENDING_APP_UPDATE_STARTED_AT_KEY) ?? "0");
  return Number.isFinite(startedAt) && startedAt > 0 && Date.now() - startedAt <= PENDING_APP_UPDATE_RECOVERY_TTL_MS;
}

function refreshWaitingState(): void {
  // Die veröffentlichte app-version.json ist die einzige Wahrheitsquelle für einen
  // sichtbaren Update-Hinweis. Ein noch vorhandener waiting-Worker darf nach einem
  // erfolgreichen Versionswechsel keinen veralteten Update-Button reaktivieren.
  const available = serverUpdateAvailable;
  const targetVersion = serverTargetVersion;
  emitUpdateState({
    available,
    targetVersion,
    noticeDismissed: Boolean(targetVersion && safeStorageGet(DISMISSED_APP_UPDATE_KEY) === targetVersion),
    error: null,
  });
}


export function hasShownCompletedMaterialentnahmeAppUpdateInIsland(version: string): boolean {
  return safeStorageGet(APP_UPDATE_SUCCESS_ISLAND_SHOWN_KEY) === version;
}

export function markCompletedMaterialentnahmeAppUpdateShownInIsland(version: string): void {
  safeStorageSet(APP_UPDATE_SUCCESS_ISLAND_SHOWN_KEY, version);
}

export function dismissMaterialentnahmeAppUpdateNotice(): void {
  const targetVersion = updateState.targetVersion;
  if (targetVersion) safeStorageSet(DISMISSED_APP_UPDATE_KEY, targetVersion);
  emitUpdateState({ noticeDismissed: true });
}

async function readPublishedServiceWorkerVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/sw.js?t=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
    if (!response.ok) return null;
    const source = await response.text();
    const match = source.match(/const CACHE_NAME = "materialentnahme-app-shell-v(\d+\.\d+\.\d+)";/);
    return match?.[1] ?? null;
  } catch { return null; }
}

async function checkServerAppVersion(): Promise<string | null> {
  startRuntimeActivity("sw:version-check", "App-Version prüfen", "service-worker", `Bundle v${APP_VERSION}`);
  try {
    const [response, publishedWorkerVersion] = await Promise.all([
      fetch(`/app-version.json?t=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      }),
      readPublishedServiceWorkerVersion(),
    ]);
    if (!response.ok) {
      recordUpdateEvent("version_check_http_error", `app-version.json HTTP ${response.status}`, null);
      finishRuntimeActivity("sw:version-check", `app-version.json HTTP ${response.status}`);
      return null;
    }
    const payload = await response.json() as { version?: string };
    const remoteVersion = payload.version?.trim() ?? "";
    safeStorageSet(INSTALLED_APP_VERSION_KEY, APP_VERSION);
    const pendingTarget = pendingUpdateTarget();
    if (pendingTarget && pendingTarget === APP_VERSION) markPendingUpdate(null);

    const newer = Boolean(remoteVersion && isNewerVersion(remoteVersion, APP_VERSION));
    const releaseConsistent = !newer || publishedWorkerVersion === remoteVersion;
    // Ein halb kopierter/fehlgeschlagener Release darf niemals als installierbares
    // Update angeboten werden. app-version.json und sw.js müssen dieselbe Zielversion
    // veröffentlichen, bevor der Button erscheint.
    serverUpdateAvailable = newer && releaseConsistent;
    serverTargetVersion = serverUpdateAvailable ? remoteVersion : null;
    refreshWaitingState();
    recordUpdateEvent(
      newer && !releaseConsistent ? "published_release_incomplete" : newer ? "update_available" : "version_check_ok",
      `Bundle v${APP_VERSION} · Server v${remoteVersion || "?"} · SW v${publishedWorkerVersion ?? "?"}`,
      remoteVersion || null,
    );
    finishRuntimeActivity(
      "sw:version-check",
      `Bundle v${APP_VERSION} · Server v${remoteVersion || "?"} · SW v${publishedWorkerVersion ?? "?"}${newer && !releaseConsistent ? " · Release noch inkonsistent" : ""}`,
    );
    return remoteVersion || null;
  } catch (error) {
    recordUpdateEvent("version_check_failed", error instanceof Error ? error.message : String(error), null);
    failRuntimeActivity("sw:version-check", error);
    // Eine fehlgeschlagene Versionsprüfung darf Offline-Betrieb und SW-Status nicht stören.
    return null;
  }
}

function updateCheckDelayMs(): number {
  const backoff = Math.min(UPDATE_CHECK_MAX_BACKOFF_MS, UPDATE_CHECK_BASE_INTERVAL_MS * (2 ** Math.min(updateCheckFailures, 3)));
  return backoff + Math.floor(Math.random() * UPDATE_CHECK_JITTER_MAX_MS);
}

function scheduleServiceWorkerUpdateCheck(registration: ServiceWorkerRegistration): void {
  if (updateCheckTimer !== null) window.clearTimeout(updateCheckTimer);
  updateCheckTimer = window.setTimeout(() => {
    updateCheckTimer = null;
    if (document.visibilityState !== "visible" || navigator.onLine === false) {
      scheduleServiceWorkerUpdateCheck(registration);
      return;
    }
    void runServiceWorkerUpdateCheck(registration, "timer");
  }, updateCheckDelayMs());
}

async function runServiceWorkerUpdateCheck(
  registration: ServiceWorkerRegistration,
  reason: "startup" | "timer" | "lifecycle" | "manual",
  force = false,
): Promise<string | null> {
  if (updateCheckPromise) return updateCheckPromise;
  if (!force && Date.now() - lastUpdateCheckAt < UPDATE_CHECK_MIN_GAP_MS) {
    scheduleServiceWorkerUpdateCheck(registration);
    return serverTargetVersion ?? APP_VERSION;
  }

  lastUpdateCheckAt = Date.now();
  updateCheckPromise = (async () => {
    startRuntimeActivity("sw:update-check", "PWA-Update prüfen", "service-worker", reason);
    try {
      const remoteVersion = await checkServerAppVersion();
      if (!remoteVersion) {
        updateCheckFailures = Math.min(updateCheckFailures + 1, 8);
        finishRuntimeActivity("sw:update-check", "Versionsprüfung ohne bestätigten Serverstand");
        return null;
      }
      try {
        await registration.update();
      } catch (error) {
        recordUpdateEvent("registration_update_failed", error instanceof Error ? error.message : String(error), remoteVersion);
        // Die veröffentlichte App-Version wurde bereits bestätigt; ein temporär
        // fehlgeschlagenes sw.js-Update darf die laufende App nicht stören.
      }
      updateCheckFailures = 0;
      finishRuntimeActivity("sw:update-check", `Server v${remoteVersion} geprüft`);
      return remoteVersion;
    } catch (error) {
      updateCheckFailures = Math.min(updateCheckFailures + 1, 8);
      failRuntimeActivity("sw:update-check", error);
      return null;
    } finally {
      updateCheckPromise = null;
      scheduleServiceWorkerUpdateCheck(registration);
    }
  })();
  return updateCheckPromise;
}

async function readServiceWorkerVersion(worker: ServiceWorker | null, timeoutMs = 1400): Promise<string | null> {
  if (!worker || typeof MessageChannel === "undefined") return null;
  return await new Promise<string | null>((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(null), timeoutMs);
    channel.port1.onmessage = (event: MessageEvent<{ version?: string }>) => {
      window.clearTimeout(timer);
      resolve(typeof event.data?.version === "string" ? event.data.version : null);
    };
    try { worker.postMessage({ type: "GET_VERSION" }, [channel.port2]); }
    catch { window.clearTimeout(timer); resolve(null); }
  });
}

async function waitForTargetWaitingWorker(
  registration: ServiceWorkerRegistration,
  targetVersion: string,
  timeoutMs = 30_000,
): Promise<ServiceWorker | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const candidate = registration.waiting;
    if (candidate) {
      const version = await readServiceWorkerVersion(candidate);
      if (version === targetVersion) return candidate;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 180));
  }
  return null;
}

function waitForControllerActivation(worker: ServiceWorker, timeoutMs = 12_000): Promise<boolean> {
  if (worker.state === "activated") return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(worker.state === "activated"), timeoutMs);
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") finish(true);
      else if (worker.state === "redundant") finish(false);
    });
    navigator.serviceWorker.addEventListener("controllerchange", () => finish(true), { once: true });
  });
}

async function activateWaitingWorker(worker: ServiceWorker, targetVersion: string | null, reloadSource: "manual" | "recovery" = "manual"): Promise<void> {
  if (targetVersion) {
    const workerVersion = await readServiceWorkerVersion(worker);
    if (workerVersion !== targetVersion) {
      throw new Error(`Der vorbereitete Service Worker gehört zu v${workerVersion ?? "unbekannt"}, erwartet wird v${targetVersion}. Er wird nicht aktiviert.`);
    }
    markPendingUpdate(targetVersion);
  }
  emitUpdateState({ applying: true, phase: "activating", error: null });
  startRuntimeActivity("sw:activate", "App-Update aktivieren", "service-worker", targetVersion ? `Ziel v${targetVersion}` : null);
  worker.postMessage({ type: "SKIP_WAITING" });
  const activated = await waitForControllerActivation(worker);
  if (!activated) {
    failRuntimeActivity("sw:activate", new Error("Aktivierungstimeout"));
    throw new Error("Der neue App-Stand konnte nicht aktiviert werden. Bitte die Verbindung prüfen und erneut versuchen.");
  }
  finishRuntimeActivity("sw:activate", targetVersion ? `v${targetVersion} aktiv` : "Service Worker aktiv");
  recordUpdateEvent("worker_activated", targetVersion ? `Service Worker v${targetVersion} aktiviert` : "Service Worker aktiviert", targetVersion);
  publishUpdateCoordination("activated", targetVersion, "Service Worker aktiviert");
  emitUpdateState({ applying: true, phase: "reloading", error: null, restartRequired: Boolean(targetVersion && targetVersion !== APP_VERSION), coordinatedByOtherTab: false });
  reloadPublishedApp(targetVersion, reloadSource);
}

function announceClientBuild(worker: ServiceWorker | null = navigator.serviceWorker?.controller ?? null): void {
  if (!worker) return;
  try { worker.postMessage({ type: "CLIENT_BUILD_VERSION", version: APP_VERSION, tabId: getPwaTabId() }); }
  catch { /* Best effort; H1 Legacy-Fallback bleibt verfügbar. */ }
}

async function recoverPendingUpdate(registration: ServiceWorkerRegistration, targetVersion: string): Promise<void> {
  if (!pendingUpdateOwnedByThisTab()) {
    recordUpdateEvent("recovery_skipped_foreign_tab", `Pending-Update gehört Tab ${pendingUpdateOwner() ?? "?"}`, targetVersion);
    return;
  }
  if (APP_VERSION === targetVersion) {
    markPendingUpdate(null);
    clearReloadGuardIfTargetReached();
    return;
  }
  // Nur einen unmittelbar zuvor von DIESEM Tab begonnenen Updatewechsel fortsetzen.
  // Shared localStorage darf niemals dazu führen, dass mehrere offene Tabs denselben
  // Waiting-Worker unabhängig aktivieren oder Reload-Schleifen starten.
  if (!pendingUpdateIsRecent()) {
    recordUpdateEvent("recovery_expired", "Pending-Update ist nicht mehr frisch", targetVersion);
    markPendingUpdate(null);
    return;
  }
  const previousAttempt = Number(safeStorageGet(PENDING_APP_UPDATE_RECOVERY_KEY) ?? "0");
  if (previousAttempt >= 1) {
    recordUpdateEvent("recovery_loop_guard", "Automatische Recovery wurde bereits versucht", targetVersion);
    markPendingUpdate(null);
    return;
  }

  const lease = tryAcquireUpdateLease(targetVersion);
  if (!lease.owned) {
    emitUpdateState({ applying: false, phase: "idle", available: true, targetVersion, coordinatedByOtherTab: true, error: null });
    recordUpdateEvent("recovery_coordinated_elsewhere", "Ein anderer Tab besitzt den Update-Lease", targetVersion);
    return;
  }

  safeStorageSet(PENDING_APP_UPDATE_RECOVERY_KEY, String(previousAttempt + 1));
  emitUpdateState({ applying: true, phase: "preparing", targetVersion, error: null, coordinatedByOtherTab: false });
  publishUpdateCoordination("applying", targetVersion, "Update-Recovery");
  recordUpdateEvent("recovery_started", `Versuch ${previousAttempt + 1}/1`, targetVersion);
  startRuntimeActivity("sw:recovery", "App-Update fortsetzen", "service-worker", `Ziel v${targetVersion}`);
  try {
    const waiting = await waitForTargetWaitingWorker(registration, targetVersion, 20_000);
    if (!waiting) {
      finishRuntimeActivity("sw:recovery", "Kein passender waiting Worker · Recovery beendet");
      markPendingUpdate(null);
      publishUpdateCoordination("failed", targetVersion, "Kein passender waiting Worker");
      recordUpdateEvent("recovery_failed", "Kein passender waiting Worker", targetVersion);
      emitUpdateState({ applying: false, phase: "idle", error: "Das zuvor gestartete Update konnte nicht eindeutig fortgesetzt werden. Bitte erneut manuell aktualisieren." });
      return;
    }
    finishRuntimeActivity("sw:recovery", `passender Worker v${targetVersion} gefunden`);
    await activateWaitingWorker(waiting, targetVersion, "recovery");
  } finally {
    lease.release();
    publishUpdateCoordination("released", targetVersion, "Update-Lease freigegeben");
  }
}

function observeRegistration(registration: ServiceWorkerRegistration): void {
  observedRegistration = registration;
  refreshWaitingState();

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;
    recordUpdateEvent("worker_installing", "Neuer Service Worker gefunden", serverTargetVersion);
    installing.addEventListener("statechange", () => {
      recordUpdateEvent(`worker_${installing.state}`, `Installationszustand: ${installing.state}`, serverTargetVersion);
      if (installing.state === "installed" && navigator.serviceWorker.controller) refreshWaitingState();
      if (installing.state === "redundant") {
        emitUpdateState({ applying: false, phase: "idle", error: "Der neue Service Worker wurde vom Browser verworfen. Die laufende App bleibt aktiv." });
      }
    });
  });

  if (!lifecycleBound) {
    lifecycleBound = true;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      void (async () => {
        const controllerVersion = await readControllerServiceWorkerVersion();
        announceClientBuild();
        recordUpdateEvent("controller_changed", controllerVersion ? `Controller v${controllerVersion}` : "Controller gewechselt", controllerVersion);
        if (controllerVersion && controllerVersion !== APP_VERSION) {
          // Multi-Tab: Ein anderer Tab kann den neuen Worker aktiviert haben. Der
          // aktuell geladene JS-Baum wird niemals automatisch mitten in einem
          // Workflow neu geladen. Stattdessen wird ein kontrollierter Neustart
          // angeboten; die H1-Vorgängercaches halten lazy Chunks bis dahin stabil.
          if (updateState.applying) {
            emitUpdateState({ phase: "reloading", targetVersion: controllerVersion, restartRequired: true, coordinatedByOtherTab: false });
          } else {
            emitUpdateState({ available: true, applying: false, phase: "idle", targetVersion: controllerVersion, restartRequired: true, coordinatedByOtherTab: false, noticeDismissed: false, error: null });
          }
          return;
        }
        if (updateState.applying) emitUpdateState({ phase: "reloading" });
      })();
    });

    subscribeUpdateCoordination((event) => {
      if (!event.targetVersion) return;
      if (event.type === "applying" && event.targetVersion !== APP_VERSION) {
        recordUpdateEvent("other_tab_applying", "Ein anderer Tab bereitet das Update vor", event.targetVersion);
        emitUpdateState({ available: true, applying: false, phase: "idle", targetVersion: event.targetVersion, coordinatedByOtherTab: true, noticeDismissed: false, error: null });
        return;
      }
      if (event.type === "activated" && event.targetVersion !== APP_VERSION) {
        recordUpdateEvent("other_tab_activated", "Neuer Worker wurde in anderem Tab aktiviert", event.targetVersion);
        emitUpdateState({ available: true, applying: false, phase: "idle", targetVersion: event.targetVersion, restartRequired: true, coordinatedByOtherTab: false, noticeDismissed: false, error: null });
        return;
      }
      if (event.type === "failed" || event.type === "released") {
        emitUpdateState({ coordinatedByOtherTab: false });
      }
    });

    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data as { type?: string; clientUuid?: string | null; contentRevision?: number; reason?: LocalWithdrawalUpdateReason; event?: string; detail?: string | null; version?: string | null } | null;
      if (data?.type === "PWA_UPDATE_LIFECYCLE" && data.event) {
        recordUpdateEvent(`sw_${data.event}`, data.detail ?? null, data.version ?? serverTargetVersion);
        if (data.event === "install_failed" || data.event === "activation_failed") {
          emitUpdateState({ applying: false, phase: "idle", available: true, targetVersion: data.version ?? serverTargetVersion, error: data.detail || "Service-Worker-Update fehlgeschlagen. Der bisherige Build bleibt aktiv." });
        }
        return;
      }
      if (data?.type === "REQUEST_CLIENT_BUILD_VERSION") {
        announceClientBuild(event.source instanceof ServiceWorker ? event.source : null);
        return;
      }
      if (data?.type !== "MATERIALENTNAHME_LOCAL_WITHDRAWAL_UPDATED" || !data.clientUuid || !data.reason) return;
      // Brücke vom Background-Sync in den bereits geöffneten React-State.
      // Es wird ausdrücklich keine Seite neu geladen.
      dispatchLocalWithdrawalUpdated(data.clientUuid, data.contentRevision, data.reason);
    });

    const requestUpdate = () => {
      if (document.visibilityState !== "visible" || navigator.onLine === false) return;
      const currentRegistration = observedRegistration;
      if (!currentRegistration) return;
      announceClientBuild();
      void runServiceWorkerUpdateCheck(currentRegistration, "lifecycle");
    };
    document.addEventListener("visibilitychange", requestUpdate);
    window.addEventListener("online", requestUpdate);
    window.addEventListener("focus", requestUpdate);
    window.addEventListener("pageshow", requestUpdate);
    announceClientBuild();
  }
}

async function probeBackendBeforeServiceWorkerMaintenance(timeoutMs = 3000): Promise<boolean> {
  if (navigator.onLine === false) return false;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch("/api/status", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function repairMaterialentnahmeServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return registerMaterialentnahmeServiceWorker();

  const backendBefore = await probeBackendBeforeServiceWorkerMaintenance();
  if (!backendBefore) {
    throw new Error("Backend-Verbindung ist vor der Service-Worker-Prüfung nicht stabil. Es wurde nichts an der Registrierung geändert.");
  }

  recordAttempt();
  startRuntimeActivity("sw:manual-repair", "Service Worker prüfen", "service-worker", `Bundle v${APP_VERSION}`);
  try {
    let registration: ServiceWorkerRegistration | null =
      (await navigator.serviceWorker.getRegistration()) ?? null;
    if (registration) {
      observeRegistration(registration);
      recordUpdateEvent("manual_existing_registration_update", `Scope ${registration.scope}`, APP_VERSION);
      await runServiceWorkerUpdateCheck(registration, "manual", true);
    } else {
      recordUpdateEvent("manual_registration_missing", "Neue Registrierung erforderlich.", APP_VERSION);
      registration = await registerMaterialentnahmeServiceWorker();
    }

    const backendAfter = await probeBackendBeforeServiceWorkerMaintenance();
    if (!backendAfter) {
      throw new Error("Backend-Verbindung ist nach der Service-Worker-Prüfung nicht mehr erreichbar. Bitte die PWA vollständig schließen und neu öffnen.");
    }
    clearRecordedError();
    finishRuntimeActivity("sw:manual-repair", registration ? "Registrierung geprüft" : "Keine Registrierung verfügbar");
    return registration;
  } catch (error) {
    recordError(error);
    failRuntimeActivity("sw:manual-repair", error);
    throw error;
  }
}

export async function registerMaterialentnahmeServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  cleanAppUpdateCacheBuster();
  clearReloadGuardIfTargetReached();
  recordAttempt();
  startRuntimeActivity("sw:register", "Service Worker registrieren", "service-worker", `Bundle v${APP_VERSION}`);
  if (!("serviceWorker" in navigator)) {
    const error = new Error(window.isSecureContext
      ? "Service Worker werden von diesem Browser nicht bereitgestellt."
      : "Service Worker benötigen einen sicheren HTTPS-Kontext. Die aktuelle Seite wird vom Browser nicht als sicherer Kontext eingestuft.");
    error.name = window.isSecureContext ? "NotSupportedError" : "SecurityError";
    recordError(error);
    failRuntimeActivity("sw:register", error);
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
    clearRecordedError();
    finishRuntimeActivity("sw:register", registration.active ? "Registrierung aktiv" : registration.waiting ? "Worker wartet" : "Registrierung vorhanden");
    observeRegistration(registration);
    // Bei jedem App-Start sowohl die veröffentlichte App-Version als auch den Service Worker prüfen.
    // Sonderfall Recovery: Wurde das neue Bundle bereits per STRG+F5/Hard-Reload vom
    // Netz geladen, ist APP_VERSION == Serverversion, während noch der alte Worker
    // kontrollieren kann. Dann darf der passende waiting-Worker automatisch übernehmen.
    void (async () => {
      const pendingTarget = pendingUpdateTarget();
      const remoteVersion = await runServiceWorkerUpdateCheck(registration, "startup", true);
      if (pendingTarget && APP_VERSION !== pendingTarget && pendingUpdateOwnedByThisTab()) {
        await recoverPendingUpdate(registration, pendingTarget).catch((error) => {
          markPendingUpdate(null);
          recordUpdateEvent("recovery_exception", error instanceof Error ? error.message : String(error), pendingTarget);
          emitUpdateState({ applying: false, phase: "idle", error: error instanceof Error ? error.message : "App-Update-Recovery fehlgeschlagen." });
        });
        return;
      }
      if (pendingTarget && APP_VERSION === pendingTarget && pendingUpdateOwnedByThisTab()) markPendingUpdate(null);
      if (!navigator.serviceWorker.controller) return;

      const controllerVersion = await readServiceWorkerVersion(navigator.serviceWorker.controller);
      if (controllerVersion && isNewerVersion(controllerVersion, APP_VERSION)) {
        emitUpdateState({ available: true, applying: false, phase: "idle", targetVersion: controllerVersion, restartRequired: true, coordinatedByOtherTab: false, noticeDismissed: false, error: null });
        recordUpdateEvent("startup_old_bundle_new_controller", `Bundle v${APP_VERSION} · Controller v${controllerVersion}`, controllerVersion);
        return;
      }

      if (remoteVersion !== APP_VERSION) return;
      // Ein Bundle kann nach Hard-Reload bereits neu sein, während noch der alte
      // Controller aktiv ist. Automatisch übernehmen darf NUR ein waiting Worker,
      // dessen eigene Version exakt dem bereits laufenden Bundle entspricht. Auch
      // dieser Recovery-Sonderfall ist Multi-Tab-koordiniert.
      const waiting = registration.waiting;
      const waitingVersion = await readServiceWorkerVersion(waiting);
      if (waiting && waitingVersion === APP_VERSION && controllerVersion !== APP_VERSION) {
        const lease = tryAcquireUpdateLease(APP_VERSION);
        if (lease.owned) {
          try {
            publishUpdateCoordination("applying", APP_VERSION, "Hard-Reload Controller-Recovery");
            waiting.postMessage({ type: "SKIP_WAITING" });
            recordUpdateEvent("controller_recovery_requested", `Waiting Worker v${APP_VERSION}`, APP_VERSION);
          } finally {
            lease.release();
            publishUpdateCoordination("released", APP_VERSION, "Controller-Recovery-Lease freigegeben");
          }
        }
      }
    })();
    return registration;
  } catch (error) {
    recordError(error);
    failRuntimeActivity("sw:register", error);
    return null;
  }
}

export async function applyMaterialentnahmeAppUpdate(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  let userTarget: string | null = null;
  let lease: ReturnType<typeof tryAcquireUpdateLease> | null = null;
  let leaseTimer: number | null = null;
  try {
    const registration = observedRegistration ?? await navigator.serviceWorker.getRegistration();
    if (!registration) throw new Error("Keine aktive Service-Worker-Registrierung gefunden.");
    observedRegistration = registration;

    // Wurde der neue Controller bereits von einem anderen Tab aktiviert, muss dieser
    // Tab nichts mehr installieren. Ein expliziter Benutzerklick lädt nur den bereits
    // aktiven neuen Build – niemals automatisch mitten in einem Workflow.
    const currentControllerVersion = await readControllerServiceWorkerVersion();
    if (updateState.restartRequired && currentControllerVersion && currentControllerVersion !== APP_VERSION) {
      userTarget = currentControllerVersion;
      recordUpdateEvent("manual_restart_after_foreign_activation", `Bundle v${APP_VERSION} → Controller v${currentControllerVersion}`, currentControllerVersion);
      try { window.sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* explicit user retry */ }
      emitUpdateState({ applying: true, phase: "reloading", targetVersion: currentControllerVersion, error: null, coordinatedByOtherTab: false });
      reloadPublishedApp(currentControllerVersion, "manual");
      return;
    }

    emitUpdateState({ applying: true, phase: "preparing", error: null, coordinatedByOtherTab: false });
    startRuntimeActivity("sw:update", "App-Update vorbereiten", "service-worker", `Bundle v${APP_VERSION}`);

    // Vor jedem Klick die Serverversion neu bestätigen. Dadurch kann ein alter
    // In-Memory-Hinweis niemals ein Update auf dieselbe oder ältere Version starten.
    const remoteVersion = await runServiceWorkerUpdateCheck(registration, "manual", true);
    if (!remoteVersion) throw new Error("Die veröffentlichte App-Version konnte nicht bestätigt werden.");
    if (!isNewerVersion(remoteVersion, APP_VERSION)) {
      serverUpdateAvailable = false;
      serverTargetVersion = null;
      if (pendingUpdateOwnedByThisTab()) markPendingUpdate(null);
      refreshWaitingState();
      finishRuntimeActivity("sw:update", `Kein Update nötig · Bundle v${APP_VERSION}, Server v${remoteVersion}`);
      emitUpdateState({ applying: false, phase: "idle", available: false, targetVersion: null, error: null, restartRequired: false, coordinatedByOtherTab: false });
      return;
    }

    const publishedWorkerVersion = await readPublishedServiceWorkerVersion();
    if (publishedWorkerVersion !== remoteVersion) {
      throw new Error(`Das Release auf dem Server ist noch nicht vollständig: app-version v${remoteVersion}, Service Worker v${publishedWorkerVersion ?? "unbekannt"}. Es wird nichts aktiviert.`);
    }

    userTarget = remoteVersion;
    lease = tryAcquireUpdateLease(userTarget);
    if (!lease.owned) {
      recordUpdateEvent("update_coordinated_elsewhere", "Ein anderer Tab besitzt den Update-Lease", userTarget);
      emitUpdateState({ available: true, applying: false, phase: "idle", targetVersion: userTarget, coordinatedByOtherTab: true, noticeDismissed: false, error: null });
      finishRuntimeActivity("sw:update", `Update v${userTarget} wird in anderem Tab koordiniert`);
      return;
    }

    publishUpdateCoordination("applying", userTarget, "Benutzerinitiiertes Update");
    recordUpdateEvent("update_apply_started", "Update-Lease übernommen", userTarget);
    markPendingUpdate(userTarget, true);
    emitUpdateState({ applying: true, phase: "preparing", targetVersion: userTarget, error: null, coordinatedByOtherTab: false });
    const scheduleLeaseRenewal = () => {
      leaseTimer = window.setTimeout(() => {
        if (!lease?.renew()) {
          recordUpdateEvent("update_lease_lost", "Update-Lease konnte nicht verlängert werden", userTarget);
          return;
        }
        scheduleLeaseRenewal();
      }, Math.max(5_000, Math.floor(UPDATE_COORDINATION_LEASE_MS / 3)));
    };
    scheduleLeaseRenewal();

    // Niemals irgendeinen waiting Worker übernehmen: Vor der Aktivierung muss
    // dessen selbst gemeldete Version exakt zur bestätigten Server-Zielversion passen.
    let waiting = registration.waiting;
    let waitingVersion = await readServiceWorkerVersion(waiting);
    if (!waiting || waitingVersion !== userTarget) {
      waiting = await waitForTargetWaitingWorker(registration, userTarget, 30_000);
      waitingVersion = await readServiceWorkerVersion(waiting);
    }
    if (!waiting || waitingVersion !== userTarget) {
      throw new Error(`Das Update auf v${userTarget} ist noch nicht vollständig installiert. Die laufende v${APP_VERSION} bleibt unverändert.`);
    }

    finishRuntimeActivity("sw:update", `waiting Worker v${waitingVersion} bereit`);
    await activateWaitingWorker(waiting, userTarget);
  } catch (error) {
    failRuntimeActivity("sw:update", error);
    if (pendingUpdateOwnedByThisTab()) markPendingUpdate(null);
    if (userTarget) {
      publishUpdateCoordination("failed", userTarget, error instanceof Error ? error.message : String(error));
      recordUpdateEvent("update_apply_failed", error instanceof Error ? error.message : String(error), userTarget);
    }
    // Nach einem fehlgeschlagenen Klick sofort erneut gegen die echte Serverversion
    // abgleichen. So verschwindet ein veralteter Button statt nach 10–30 s einfach
    // kommentarlos wieder aufzutauchen.
    const registration = observedRegistration;
    if (registration) await runServiceWorkerUpdateCheck(registration, "manual", true).catch(() => null);
    emitUpdateState({ applying: false, phase: "idle", error: error instanceof Error ? error.message : "App-Update konnte nicht aktiviert werden.", targetVersion: userTarget ?? serverTargetVersion, coordinatedByOtherTab: false });
  } finally {
    if (leaseTimer !== null) window.clearInterval(leaseTimer);
    if (lease?.owned) {
      lease.release();
      publishUpdateCoordination("released", userTarget, "Update-Lease freigegeben");
    }
  }
}


async function probeScript(): Promise<boolean | null> {
  try {
    const response = await fetch("/sw.js", { method: "GET", cache: "no-store", credentials: "same-origin" });
    return response.ok;
  } catch {
    return false;
  }
}

function stateFor(registration: ServiceWorkerRegistration | null, errorName: string | null): ServiceWorkerDiagnostics["registrationState"] {
  if (!("serviceWorker" in navigator)) return "nicht unterstützt";
  if (registration?.waiting) return "wartend";
  if (registration?.active) return "aktiv";
  if (registration?.installing) return "installiert";
  if (errorName) return "fehler";
  return "nicht registriert";
}

async function readPublishedAppVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/app-version.json?t=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
    if (!response.ok) return null;
    const payload = await response.json() as { version?: string };
    return typeof payload.version === "string" ? payload.version : null;
  } catch { return null; }
}

async function readControllerServiceWorkerVersion(): Promise<string | null> {
  return readServiceWorkerVersion(navigator.serviceWorker?.controller ?? null, 1200);
}

async function readPwaCacheDiagnostics(): Promise<{ appShellCacheVersions: string[]; runtimeAssetCachePresent: boolean }> {
  if (!("caches" in window)) return { appShellCacheVersions: [], runtimeAssetCachePresent: false };
  try {
    const names = await caches.keys();
    const prefix = "materialentnahme-app-shell-v";
    const appShellCacheVersions = names
      .filter((name) => name.startsWith(prefix))
      .map((name) => name.slice(prefix.length))
      .filter((value) => /^\d+\.\d+\.\d+$/.test(value))
      .sort((left, right) => {
        if (left === right) return 0;
        return isNewerVersion(left, right) ? -1 : 1;
      });
    return { appShellCacheVersions, runtimeAssetCachePresent: names.includes("materialentnahme-runtime-assets-v1") };
  } catch { return { appShellCacheVersions: [], runtimeAssetCachePresent: false }; }
}

export async function getServiceWorkerDiagnostics(): Promise<ServiceWorkerDiagnostics> {
  const supported = "serviceWorker" in navigator;
  let registration: ServiceWorkerRegistration | null = null;
  let lookupError: unknown = null;

  if (supported) {
    try { registration = (await navigator.serviceWorker.getRegistration()) ?? null; }
    catch (error) { lookupError = error; recordError(error); }
  }

  const errorName = safeStorageGet(ERROR_NAME_KEY) ?? (lookupError instanceof Error ? lookupError.name : null);
  const errorMessage = safeStorageGet(ERROR_MESSAGE_KEY) ?? (lookupError instanceof Error ? lookupError.message : null);
  const [scriptReachable, controllerVersion, publishedVersion, installingVersion, waitingVersion, cacheDiagnostics] = await Promise.all([
    probeScript(),
    supported ? readControllerServiceWorkerVersion() : Promise.resolve(null),
    readPublishedAppVersion(),
    readServiceWorkerVersion(registration?.installing ?? null),
    readServiceWorkerVersion(registration?.waiting ?? null),
    readPwaCacheDiagnostics(),
  ]);
  const knownVersions = [APP_VERSION, controllerVersion, publishedVersion].filter((value): value is string => Boolean(value));
  const versionsMatch = knownVersions.length >= 2 ? new Set(knownVersions).size === 1 : null;
  const pendingTargetVersion = pendingUpdateTarget();
  const restartRequired = updateState.restartRequired || Boolean(controllerVersion && isNewerVersion(controllerVersion, APP_VERSION));

  return {
    supported,
    secureContext: window.isSecureContext,
    origin: window.location.origin,
    registrationState: stateFor(registration, errorName),
    scope: registration?.scope ?? null,
    controllerActive: Boolean(navigator.serviceWorker?.controller),
    scriptReachable,
    lastAttemptAt: safeStorageGet(LAST_ATTEMPT_KEY),
    errorName,
    errorMessage,
    bundleVersion: APP_VERSION,
    controllerVersion,
    publishedVersion,
    versionsMatch,
    installingVersion,
    waitingVersion,
    pendingTargetVersion,
    pendingOwnedByThisTab: pendingUpdateOwnedByThisTab(),
    restartRequired,
    updateCheckFailures,
    lastUpdateCheckAt: lastUpdateCheckAt > 0 ? new Date(lastUpdateCheckAt).toISOString() : null,
    recentUpdateEvents: [...readUpdateEventLog()].reverse(),
    appShellCacheVersions: cacheDiagnostics.appShellCacheVersions,
    runtimeAssetCachePresent: cacheDiagnostics.runtimeAssetCachePresent,
  };
}

export function serviceWorkerStatusLabel(diagnostics: ServiceWorkerDiagnostics): string {
  if (!diagnostics.supported) return "Nicht unterstützt";
  if (diagnostics.restartRequired) return "Neuer Build aktiv · Neustart ausstehend";
  if (diagnostics.registrationState === "aktiv") return diagnostics.controllerActive ? "Aktiv" : "Registriert · Neustart ausstehend";
  if (diagnostics.registrationState === "fehler") return "Registrierung fehlgeschlagen";
  if (diagnostics.registrationState === "wartend") return "Update wartet";
  if (diagnostics.registrationState === "installiert") return "Wird installiert";
  return "Nicht registriert";
}


const MATERIALENTNAHME_BACKGROUND_SYNC_TAG = "materialentnahme-withdrawals";

export async function registerMaterialentnahmeBackgroundSync(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;
  try {
    const registration = (await navigator.serviceWorker.getRegistration()) ?? await registerMaterialentnahmeServiceWorker();
    if (!registration) return false;
    const syncRegistration = registration as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    };
    if (!syncRegistration.sync?.register) return false;
    await syncRegistration.sync.register(MATERIALENTNAHME_BACKGROUND_SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}

export async function updateMaterialentnahmeAppBadge(count: number): Promise<void> {
  const badgeNavigator = navigator as Navigator & {
    setAppBadge?: (contents?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0 && badgeNavigator.setAppBadge) await badgeNavigator.setAppBadge(count);
    else if (count <= 0 && badgeNavigator.clearAppBadge) await badgeNavigator.clearAppBadge();
  } catch {
    // Badging ist Best-Effort und darf Offline-/Sync-Logik niemals blockieren.
  }
}
